import { toast } from 'sonner'

import { FormEvent, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Label, PosButton, Spinner } from '@/components/ui'
import { useSession } from '@/contexts/SessionContext'

/**
 * Sync is waiting for a password sign-in (no token, or it expired). Asked between sales, never during one, and
 * closable: selling carries on locally either way (§15.10 Q30). The cashier does not change.
 */
export const SyncSignInDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const { login } = useSession()
  const [busy, setBusy] = useState(false)

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    setBusy(true)
    try {
      await login(String(form.get('email')).trim(), String(form.get('password')), { keepCashier: true })
      toast.success('Signed in. Syncing.')
      onOpenChange(false)
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sign in to sync</DialogTitle>
          <DialogDescription>Sales are saved on this device and will be sent once someone signs in.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="sync-email">Email</Label>
            <Input id="sync-email" name="email" type="email" autoComplete="username" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sync-password">Password</Label>
            <Input id="sync-password" name="password" type="password" autoComplete="current-password" required />
          </div>
          <div className="flex gap-2">
            <PosButton type="submit" size="touch" className="flex-1" disabled={busy}>
              {busy ? <Spinner /> : 'Sign in'}
            </PosButton>
            <PosButton type="button" size="touch" variant="secondary" onClick={() => onOpenChange(false)}>
              Later
            </PosButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
